/**
 * Environment Configuration Module
 *
 * Provides type-safe access to environment variables with validation,
 * defaults, and helpful error messages.
 *
 * Validation is powered by Zod using the shared shape in `env.schema.ts`. The
 * same schema is enforced at BUILD time in `vite.config.ts` (so `vite build`
 * fails on missing/invalid vars) and here at app runtime.
 *
 * Usage:
 *   import { env } from '@/config/env';
 *   const apiUrl = env.api.baseUrl;
 */
import { validateEnv } from '@skewb/env';
import { clientEnvSchema } from './env.schema';

/**
 * Log levels in order of severity
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

/**
 * Environment names
 */
export type Environment = 'development' | 'qa' | 'staging' | 'production' | 'sys';

/**
 * Theme options
 */
export type Theme = 'light' | 'dark' | 'system';

/**
 * Validated, typed environment variables. Reading this object triggers eager
 * validation on module load; a missing required var (e.g. VITE_API_BASE_URL)
 * throws an aggregated error.
 */
const parsedEnv = validateEnv(
  clientEnvSchema,
  import.meta.env as Record<string, string | undefined>,
  'startup',
  'Check your .env file against .env.example.',
);

/**
 * Build timestamp (populated during build process, falls back to now in dev)
 */
const getBuildDate = (): string => parsedEnv.VITE_BUILD_DATE ?? new Date().toISOString();

/**
 * API Configuration
 */
export const apiConfig = {
  baseUrl: parsedEnv.VITE_API_BASE_URL,
  timeout: parsedEnv.VITE_API_TIMEOUT,
  maxRetries: parsedEnv.VITE_API_MAX_RETRIES,
} as const;

/**
 * Application Metadata
 */
export const appConfig = {
  version: parsedEnv.VITE_APP_VERSION,
  buildDate: getBuildDate(),
  environment: parsedEnv.VITE_ENVIRONMENT,
  name: 'Skewb Works Lite',
} as const;

/**
 * Feature Flags
 */
export const featureFlags = {
  analytics: parsedEnv.VITE_ENABLE_ANALYTICS,
  debug: parsedEnv.VITE_ENABLE_DEBUG,
  serviceWorker: parsedEnv.VITE_ENABLE_SERVICE_WORKER,
  mockApi: parsedEnv.VITE_ENABLE_MOCK_API,
} as const;

/**
 * Logging Configuration
 */
export const loggingConfig = {
  level: parsedEnv.VITE_LOG_LEVEL,
  enableConsole: parsedEnv.VITE_ENABLE_CONSOLE_LOGS,
} as const;

/**
 * External Services Configuration
 */
export const servicesConfig = {
  analyticsId: parsedEnv.VITE_ANALYTICS_ID,
  errorTrackingDsn: parsedEnv.VITE_ERROR_TRACKING_DSN,
  mapApiKey: parsedEnv.VITE_MAP_API_KEY,
} as const;

/**
 * Performance & Optimization Configuration
 */
export const performanceConfig = {
  enableReactQueryDevtools: parsedEnv.VITE_ENABLE_REACT_QUERY_DEVTOOLS,
  cacheDuration: parsedEnv.VITE_CACHE_DURATION,
  cacheStaleTime: parsedEnv.VITE_CACHE_STALE_TIME,
} as const;

/**
 * Security Configuration
 */
export const securityConfig = {
  enableCredentials: parsedEnv.VITE_ENABLE_CREDENTIALS,
  authHeaderName: parsedEnv.VITE_AUTH_HEADER_NAME,
} as const;

/**
 * UI Configuration
 */
export const uiConfig = {
  defaultPageSize: parsedEnv.VITE_DEFAULT_PAGE_SIZE,
  defaultTheme: parsedEnv.VITE_DEFAULT_THEME,
  dateFormat: parsedEnv.VITE_DATE_FORMAT,
  timeFormat: parsedEnv.VITE_TIME_FORMAT,
} as const;

/**
 * Dashboard Configuration
 */
export const dashboardConfig = {
  refreshIntervalMs: parsedEnv.VITE_DASHBOARD_REFRESH_INTERVAL_MS,
  enableAutoRefresh: parsedEnv.VITE_DASHBOARD_AUTO_REFRESH,
} as const;

/**
 * Aggregated environment configuration
 */
export const env = {
  api: apiConfig,
  app: appConfig,
  features: featureFlags,
  logging: loggingConfig,
  services: servicesConfig,
  performance: performanceConfig,
  security: securityConfig,
  ui: uiConfig,
  dashboard: dashboardConfig,

  /**
   * Utility methods
   */
  isDevelopment: appConfig.environment === 'development',
  isQa: appConfig.environment === 'qa',
  isStaging: appConfig.environment === 'staging',
  isProduction: appConfig.environment === 'production',

  /**
   * Check if a log level should be logged
   */
  shouldLog: (level: LogLevel): boolean => {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'none'];
    const currentLevelIndex = levels.indexOf(loggingConfig.level);
    const checkLevelIndex = levels.indexOf(level);
    return checkLevelIndex >= currentLevelIndex && loggingConfig.enableConsole;
  },
} as const;

// Helpful development logging + production sanity warnings (parity with the
// previous validateEnv() behaviour; hard validation is now handled by Zod above).
if (env.isProduction && env.features.analytics && !env.services.analyticsId) {
  console.warn('Warning: Analytics is enabled but VITE_ANALYTICS_ID is not set');
}
if (env.isProduction && env.features.debug) {
  console.warn('Warning: Debug mode is enabled in production');
}
if (env.isDevelopment && env.shouldLog('info')) {
  console.log('🚀 Works Lite Environment Configuration:');
  console.log(`   Environment: ${env.app.environment}`);
  console.log(`   Version: ${env.app.version}`);
  console.log(`   API Base URL: ${env.api.baseUrl}`);
  console.log(`   Debug Mode: ${env.features.debug ? 'ON' : 'OFF'}`);
  console.log(`   Log Level: ${env.logging.level}`);
}

export default env;
